/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Late work rule row
 *
 * @author Satyaki Bose
 * @file LateWorkRuleRow.jsx
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  FormControlLabel,
  TextField,
  Typography
} from '@mui/material';
import { Controller } from 'react-hook-form';
import LateWorkDeadlineSelect from './LateWorkDeadlineSelect';
import CheckboxCheckedIcon from '../../icons/CheckboxCheckedIcon';
import CheckboxOutlineIcon from '../../icons/CheckboxOutlineIcon';
import { resolveAssignmentSettingsVisibility } from './utils/assignmentSettingsFormConfig';
import { normalizeLateWorkPercentInput } from './utils/assignmentSettingsFormUtils';

const defaultVisibility = resolveAssignmentSettingsVisibility({});

const LateWorkRuleRow = ({
  classes,
  control,
  textInfo,
  disabled,
  visibility: visibilityProp,
  selectConfig,
  lateWorkDeadlineOptions,
  allowLateWork,
  lateWorkPercentRules,
  lateWorkCustomDateRules,
  onAllowLateWorkChange,
  onLateWorkDeadlineChange,
  onFieldChange
}) => {
  const visibility = visibilityProp || defaultVisibility;
  const isRuleGatedByAllowLateWork = visibility.isAllowLateWorkCheckboxVisible;
  const isRuleDisabled = disabled || (isRuleGatedByAllowLateWork && !allowLateWork);

  const lateWorkPercentClassName = [
    classes.lateWorkPercentField,
    isRuleDisabled ? classes.lateWorkPercentFieldDisabled : ''
  ].filter(Boolean).join(' ');

  const lateWorkRuleRowClassName = [
    classes.lateWorkRuleRow,
    isRuleDisabled ? classes.lateWorkRuleRowDisabled : ''
  ].filter(Boolean).join(' ');

  const showRulePrefixText = visibility.isLateWorkPercentFieldVisible
    || visibility.isLateWorkDeadlineFieldVisible;
  const showRuleSuffixText = visibility.isLateWorkPercentFieldVisible
    || visibility.isLateWorkDeadlineFieldVisible;

  return (
    <div className={classes.lateWorkContent}>
      {visibility.isAllowLateWorkCheckboxVisible ? (
        <div className={classes.allowLateWorkRow}>
          <FormControlLabel
            classes={{
              root: classes.allowLateWorkControl,
              label: classes.allowLateWorkLabel
            }}
            control={(
              <Checkbox
                checked={Boolean(allowLateWork)}
                onChange={(event) => onAllowLateWorkChange?.(event.target.checked)}
                color="primary"
                disableRipple
                disabled={disabled}
                className={classes.allowLateWorkCheckbox}
                icon={(
                  <CheckboxOutlineIcon
                    className={classes.allowLateWorkCheckboxIcon}
                  />
                )}
                checkedIcon={(
                  <CheckboxCheckedIcon
                    className={classes.allowLateWorkCheckboxIcon}
                  />
                )}
              />
            )}
            label={textInfo.allowLateWorkLabel}
          />
        </div>
      ) : null}

      {visibility.isLateWorkRuleRowVisible ? (
        <div
          className={lateWorkRuleRowClassName}
          aria-disabled={isRuleDisabled}
        >
          {showRulePrefixText ? (
            <Typography component="span" className={classes.lateWorkText}>
              {textInfo.lateWorkPrefix}
            </Typography>
          ) : null}

          {visibility.isLateWorkPercentFieldVisible ? (
            <Controller
              name="lateWorkPercent"
              control={control}
              rules={{ validate: lateWorkPercentRules }}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  className={lateWorkPercentClassName}
                  disabled={isRuleDisabled}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 3,
                    'aria-label': textInfo.lateWorkPrefix
                  }}
                  onChange={(event) => {
                    const nextValue = normalizeLateWorkPercentInput(event.target.value);
                    field.onChange(nextValue);
                    onFieldChange?.('lateWorkPercent', nextValue);
                  }}
                />
              )}
            />
          ) : null}

          {visibility.isLateWorkPercentFieldVisible ? (
            <Typography component="span" className={classes.lateWorkText}>
              {textInfo.lateWorkMiddle}
            </Typography>
          ) : null}

          {visibility.isLateWorkDeadlineFieldVisible ? (
            <Controller
              name="lateWorkDeadlineType"
              control={control}
              render={({ field: deadlineField }) => (
                <Controller
                  name="lateWorkCustomDate"
                  control={control}
                  rules={{ validate: lateWorkCustomDateRules }}
                  render={({ field: customDateField }) => (
                    <LateWorkDeadlineSelect
                      className={classes.dropdownField}
                      value={deadlineField.value}
                      options={lateWorkDeadlineOptions}
                      customDateValue={customDateField.value}
                      disabled={isRuleDisabled}
                      showCustomDatePanel={visibility.isLateWorkCustomDateFieldVisible}
                      config={selectConfig}
                      textInfo={textInfo}
                      onDeadlineChange={(nextValue) => {
                        if (!nextValue) {
                          return;
                        }
                        deadlineField.onChange(nextValue);
                        onLateWorkDeadlineChange?.(nextValue);
                        onFieldChange?.('lateWorkDeadlineType', nextValue);
                      }}
                      onCustomDateChange={(nextDate) => {
                        customDateField.onChange(nextDate);
                        onFieldChange?.('lateWorkCustomDate', nextDate);
                      }}
                      onCustomDateApply={(nextDate) => {
                        onFieldChange?.('lateWorkCustomDate', nextDate);
                      }}
                    />
                  )}
                />
              )}
            />
          ) : null}

          {showRuleSuffixText ? (
            <Typography component="span" className={classes.lateWorkText}>
              {textInfo.lateWorkSuffix}
            </Typography>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

LateWorkRuleRow.propTypes = {
  classes: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
  textInfo: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  visibility: PropTypes.object,
  selectConfig: PropTypes.shape({
    showSelectedCheckIcon: PropTypes.bool
  }),
  lateWorkDeadlineOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })),
  allowLateWork: PropTypes.bool,
  lateWorkPercentRules: PropTypes.func,
  lateWorkCustomDateRules: PropTypes.func,
  onAllowLateWorkChange: PropTypes.func,
  onLateWorkDeadlineChange: PropTypes.func,
  onFieldChange: PropTypes.func
};

LateWorkRuleRow.defaultProps = {
  disabled: false,
  visibility: null,
  selectConfig: {},
  lateWorkDeadlineOptions: [],
  allowLateWork: true,
  lateWorkPercentRules: undefined,
  lateWorkCustomDateRules: undefined,
  onAllowLateWorkChange: null,
  onLateWorkDeadlineChange: null,
  onFieldChange: null
};

export default LateWorkRuleRow;
